
import React from 'react';
import { format, isToday, isTomorrow, differenceInDays, differenceInHours } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, ChevronRight, Clock, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import type { Reminder } from '@/components/workspace/RemindersComponent';

interface RemindersSectionProps {
  upcomingReminders: Reminder[];
  reminderCount: number;
  onNewReminderClick: (e: React.MouseEvent) => void;
  onNavigateToReminders: (reminderId?: string) => void;
}

const RemindersSection: React.FC<RemindersSectionProps> = ({
  upcomingReminders,
  reminderCount,
  onNewReminderClick,
  onNavigateToReminders
}) => {
  const formatReminderDate = (dueDate: string) => {
    const date = new Date(dueDate);
    return format(date, "d 'de' MMMM, yyyy • HH:mm", { locale: es });
  };

  const getReminderStatus = (dueDate: string) => {
    const date = new Date(dueDate);
    const now = new Date();
    
    // To compare just the dates without time
    const todayDate = new Date(now);
    todayDate.setHours(0, 0, 0, 0);
    const dueDateOnly = new Date(date);
    dueDateOnly.setHours(0, 0, 0, 0);
    
    const daysLeft = Math.ceil((dueDateOnly.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));
    const hoursLeft = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    // 1. Vencido (Fecha ya pasada)
    if (daysLeft < 0) {
      return { 
        label: `Atrasado por ${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? 'día' : 'días'}`,
        bgColor: 'bg-red-50', 
        borderColor: 'border-red-200 border-l-4 border-red-500',
        textColor: 'text-red-700',
        badgeColor: 'bg-red-100 text-red-700',
        icon: <AlertTriangle className="h-3 w-3 mr-1" />,
        priority: 0 // Highest priority for sorting
      };
    }
    
    // 2. Hoy (Mismo día)
    if (isToday(date)) {
      const hoursRemaining = Math.max(1, hoursLeft);
      return { 
        label: `Hoy (${hoursRemaining}h)`,
        bgColor: 'bg-orange-50', 
        borderColor: 'border-orange-200 border-l-4 border-orange-500',
        textColor: 'text-orange-700',
        badgeColor: 'bg-orange-100 text-orange-700',
        icon: <Clock className="h-3 w-3 mr-1" />,
        priority: 1
      };
    }
    
    // 3. Mañana (Un día)
    if (isTomorrow(date)) {
      return { 
        label: 'Mañana',
        bgColor: 'bg-amber-50', 
        borderColor: 'border-amber-200 border-l-4 border-amber-500', 
        textColor: 'text-amber-700',
        badgeColor: 'bg-amber-100 text-amber-700',
        icon: null,
        priority: 2
      };
    }
    
    // 4. Esta semana (2-5 días)
    if (daysLeft >= 2 && daysLeft <= 5) {
      return { 
        label: `En ${daysLeft} días`,
        bgColor: 'bg-blue-50', 
        borderColor: 'border-blue-200 border-l-4 border-blue-500', 
        textColor: 'text-blue-700',
        badgeColor: 'bg-blue-100 text-blue-700',
        icon: null,
        priority: 3
      };
    }
    
    // 5. Próxima semana y más allá (>5 días)
    return { 
      label: `En ${daysLeft} días`,
      bgColor: 'bg-gray-50', 
      borderColor: 'border-gray-200 border-l-4 border-gray-400', 
      textColor: 'text-gray-700',
      badgeColor: 'bg-gray-100 text-gray-700',
      icon: null,
      priority: 4
    };
  };

  // Sort reminders by priority (most urgent first)
  const sortedReminders = [...upcomingReminders].sort((a, b) => {
    const statusA = getReminderStatus(a.due_date);
    const statusB = getReminderStatus(b.due_date);
    return statusA.priority - statusB.priority;
  });

  return (
    <div className="mt-auto">
      <div className="px-4 pt-4 pb-2 border-t border-gray-100">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Próximos Recordatorios</h3>
          <button 
            onClick={() => onNavigateToReminders()}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
          >
            Ver todos
            <ArrowRight className="h-3 w-3 ml-1" />
          </button>
        </div>
        
        {sortedReminders.length > 0 ? (
          <>
            {sortedReminders.slice(0, 2).map((reminder) => {
              const status = getReminderStatus(reminder.due_date);
              return (
                <div key={reminder.id} className="px-2 mb-2">
                  <div 
                    className={`p-3 rounded-md border ${status.borderColor} ${status.bgColor} hover:bg-opacity-80 cursor-pointer transition-all duration-200`}
                    onClick={() => onNavigateToReminders(reminder.id)}
                  >
                    <div className={`font-medium ${status.textColor}`}>{reminder.title}</div>
                    <div className="text-xs text-gray-500 mt-1">{formatReminderDate(reminder.due_date)}</div>
                    <div className="mt-2">
                      <span className={`px-2 py-0.5 text-xs ${status.badgeColor} rounded-md font-medium inline-flex items-center`}>
                        {status.icon}
                        {status.label}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className="px-2 mb-2 text-center py-3 bg-gray-50 rounded-md border border-gray-200">
            <p className="text-sm text-gray-500">No hay recordatorios próximos</p>
          </div>
        )}
        
        {/* Nuevo recordatorio */}
        <div className="px-2 mb-3">
          <button 
            onClick={onNewReminderClick}
            className="flex items-center justify-center w-full p-2 rounded-md border border-blue-100 bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            Nuevo recordatorio
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemindersSection;
